import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import { MAPPING_TYPE_RATE_CODE } from 'lumen/utils/constants/pms-mapping-constants';
import { timeout } from 'lumen/utils/waiters';

interface HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitleSignature {
  Args: {
    extra?: {
      hasHotelsThatMapByRateCode: boolean;
    };
    isDisabled?: boolean;
    params?: {
      isLoading: boolean;
      searchValues?: {
        isSearchActive: boolean;
        searchValue: string;
      };
      segmentationName: string;
      showExpandAndSearch: boolean;
      updateIsSearchActive: (value: boolean) => void;
      updateSearchValue: (value: string) => void;
    };
  };
  Blocks: {
    default: [];
  };
}

// Remove the wrapping div so the child content is a direct child of the prism-card and can use the slots
export default class HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitle extends Component<HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitleSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  @tracked tempSearchValue = '';

  /**
   * This updates the search value in the controller
   * We keep using the temp search value at all times and assume nothing went wrong updating the value
   * If something did go wrong, we'll send the correct value down to the controller next time
   */
  updateSearchValueTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      const { params } = this.args;

      this.tempSearchValue = (event.target as HTMLInputElement).value;
      await timeout();
      params?.updateSearchValue(this.tempSearchValue);
    },
  );

  get infoText(): string {
    if (this.segmentationName === RI_BREAKDOWNS.BOOKING_LEAD_TIME) {
      if (this.access.hasFeature('pms.transient_group_split')) {
        return this.intl.t('revenue_insight.booking_window_explanation');
      }

      return this.intl.t('revenue_insight.booking_window_explanation.no_split');
    }

    if (
      this.segmentationName === RI_BREAKDOWNS.SEGMENT &&
      this.riSelections.isTargetActive
    ) {
      if (
        this.riSelections.isSingleProperty &&
        this.hotelAndMarketService.hotel
          ?.revenueInsightMappingSegmentBreakdown === MAPPING_TYPE_RATE_CODE
      ) {
        return this.intl.t(
          'revenue_insight.segment_by_rate_code_target_unavailable',
        );
      }

      if (
        this.riSelections.isMultiProperty &&
        this.args.extra?.hasHotelsThatMapByRateCode
      ) {
        return this.intl.t(
          'revenue_insight.multi.segment_by_rate_code_target_unavailable',
        );
      }
    }

    return '';
  }

  get searchPlaceholder(): string {
    const segmentationName =
      this.riSelections.isCancellationsOnlyView &&
      this.segmentationName === RI_BREAKDOWNS.PICKUP
        ? 'cancellation_pickup'
        : this.segmentationName;

    return this.intl.t(`revenue_insight.search.${segmentationName}`);
  }

  get segmentationName(): string | undefined {
    const { params } = this.args;

    return params?.segmentationName;
  }

  get showBreakdownDetail(): boolean {
    // Hide breakdown detail for cancellation leadtime in percentage view
    return !(
      this.riSelections.isCancellationsPercentageView &&
      this.segmentationName === RI_BREAKDOWNS.CANCELLATION_LEAD_TIME
    );
  }

  @action enableSearch(enable: boolean): void {
    const { params } = this.args;

    params?.updateIsSearchActive(enable);
  }

  <template>
    <div class="card-title-header card-header">
      {{#if @params.isLoading}}
        <div class="title-box flex items-center">
          <HotelAndMarketRevenueInsightLoadingStateTitle />
        </div>
      {{else if @params.searchValues.isSearchActive}}
        <div class="flex flex-grow">
          <PrismInput
            @autofocus={{true}}
            @iconAfter={{hash name="search"}}
            @onInput={{this.updateSearchValueTask.perform}}
            @placeholder={{this.searchPlaceholder}}
            @removePadding={{true}}
            @value={{this.tempSearchValue}}
            {{outsideClickHandler (fn this.enableSearch false)}}
          />
        </div>
      {{else}}
        <div class="title-box flex items-center gap-100">
          {{yield}}

          {{#if this.infoText}}
            <GeneralIconWithTooltip
              @side="top"
              @tooltipContent={{this.infoText}}
            />
          {{/if}}

          {{#if @isDisabled}}
            <span>
              <PrismBadge
                @iconName="circle-information-outline"
                @label={{t "general.filtering_unavailable"}}
              />
              <PrismTooltip
                @text={{t "revenue_insight.filtering_unavailable.explanation"}}
              />
            </span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
